import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Loader2,
  PauseCircle,
  PlayCircle,
  Trash2,
  X,
} from "lucide-react";
import { fixtureMatchLabel, formatFixtureKickoff } from "../../utils/fixturesDeskHelpers";

export const FIXTURE_CONFIRM = {
  SUSPEND: "suspend",
  UNSUSPEND: "unsuspend",
  REMOVE: "remove",
};

const CONFIG = {
  [FIXTURE_CONFIRM.SUSPEND]: {
    eyebrow: "Suspend fixture",
    title: "Suspend this game?",
    icon: PauseCircle,
    iconClass: "fd-confirm-icon-warn",
    sheetClass: "fd-confirm-sheet-warn",
    confirmClass: "fd-confirm-warn",
    confirmLabel: "Suspend fixture",
    caution:
      "Bettors will not be able to add new legs on this game while it is suspended. Existing open tickets are not affected.",
  },
  [FIXTURE_CONFIRM.UNSUSPEND]: {
    eyebrow: "Unsuspend fixture",
    title: "Resume this game?",
    icon: PlayCircle,
    iconClass: "fd-confirm-icon-success",
    sheetClass: "fd-confirm-sheet-success",
    confirmClass: "fd-confirm-success",
    confirmLabel: "Unsuspend fixture",
    caution: "The fixture will accept new tickets again once unsuspended.",
  },
  [FIXTURE_CONFIRM.REMOVE]: {
    eyebrow: "Remove fixture",
    title: "Take off the board?",
    icon: Trash2,
    iconClass: "fd-confirm-icon-danger",
    sheetClass: "fd-confirm-sheet-danger",
    confirmClass: "fd-confirm-danger",
    confirmLabel: "Remove fixture",
    caution:
      "You cannot remove a fixture that has active tickets on it, or if doing so would drop below your minimum legs while the house is active.",
  },
};

const FixtureActionConfirmModal = ({ open, kind, item, busy, onClose, onConfirm }) => {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, busy, onClose]);

  const cfg = useMemo(() => (kind ? CONFIG[kind] : null), [kind]);

  if (!open || !item || !cfg) return null;

  const label = fixtureMatchLabel(item);
  const Icon = cfg.icon;
  const isRemove = kind === FIXTURE_CONFIRM.REMOVE;

  return createPortal(
    <div
      className="fd-confirm-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fd-action-confirm-title"
    >
      <button
        type="button"
        className="fd-confirm-backdrop"
        aria-label="Close"
        onClick={() => !busy && onClose()}
      />
      <div className={`fd-confirm-sheet ${cfg.sheetClass}`}>
        <header className="fd-confirm-head">
          <span className={`fd-confirm-icon ${cfg.iconClass}`}>
            <Icon size={18} />
          </span>
          <div className="fd-confirm-head-copy">
            <p className="fd-confirm-eyebrow">{cfg.eyebrow}</p>
            <h2 id="fd-action-confirm-title">{cfg.title}</h2>
          </div>
          <button
            type="button"
            className="fd-confirm-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="fd-confirm-body">
          <div className="fd-confirm-match">
            <span className="fd-confirm-match-league">{item.league?.name || "League"}</span>
            <strong className="fd-confirm-match-teams">{label}</strong>
            <time className="fd-confirm-match-time" dateTime={item.date}>
              {formatFixtureKickoff(item)}
            </time>
          </div>

          <p className="fd-confirm-copy">
            {isRemove ? (
              <>
                This game will be removed from your house board. Associated odds for this
                fixture will be deleted.
              </>
            ) : kind === FIXTURE_CONFIRM.SUSPEND ? (
              <>New tickets will be blocked on this fixture until you unsuspend it.</>
            ) : (
              <>This fixture will accept new tickets again once unsuspended.</>
            )}
          </p>

          <div className="fd-confirm-caution">
            <AlertTriangle size={14} aria-hidden />
            <span>{cfg.caution}</span>
          </div>
        </div>

        <footer className="fd-confirm-foot">
          <button type="button" className="fd-confirm-cancel" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={cfg.confirmClass}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />}
            <span>{cfg.confirmLabel}</span>
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default FixtureActionConfirmModal;
